import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Panel, Btn, StatusPill, fmt, inputCls, useOps } from "./ui";
import PlayerDetail from "./PlayerDetail";

function Deletions({ onChange }) {
  const { api } = useOps();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/deletions").then((r) => setRows(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, a) => {
    if (a === "approve" && !window.confirm("Delete this player and all their media?")) return;
    await api.post(`/deletions/${id}/${a}`); toast.success(a === "approve" ? "Deleted" : "Dismissed"); load(); onChange();
  };
  if (!rows.length) return null;
  return (
    <Panel title={`Deletion requests (${rows.length})`} testid="admin-deletion-requests">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-3 border-b border-[#243144] py-1.5 text-xs last:border-0">
          <span className="mono">{r.serial}</span><span className="text-slate-400">{r.email}</span><span className="text-slate-500">{fmt(r.created_at)}</span>
          <span className="ml-auto flex gap-2">
            <Btn variant="danger" onClick={() => act(r.id, "approve")} data-testid={`deletion-approve-${r.id}`}>Delete</Btn>
            <Btn variant="ghost" onClick={() => act(r.id, "dismiss")} data-testid={`deletion-dismiss-${r.id}`}>Dismiss</Btn>
          </span>
        </div>
      ))}
    </Panel>
  );
}

export default function Players() {
  const { api, focusPlayer, setFocusPlayer } = useOps();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const reqSeq = useRef(0);
  const search = useCallback(() => {
    const seq = (reqSeq.current += 1);
    return api.get("/players", { params: { q } }).then((r) => { if (seq === reqSeq.current) setRows(r.data); }).catch((e) => toast.error(errText(e)));
  }, [api, q]);
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid gap-3 lg:grid-cols-[380px_1fr]">
      <div className="space-y-3">
        <Deletions onChange={search} />
        <Panel title="Player lookup" testid="admin-player-lookup">
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); search(); }}>
            <input data-testid="admin-player-search-input" className={inputCls} placeholder="email or serial" value={q} onChange={(e) => setQ(e.target.value)} />
            <Btn type="submit" data-testid="admin-player-search-button">Search</Btn>
          </form>
          <div className="mt-3 max-h-[65vh] overflow-auto">
            {rows.map((p) => (
              <button key={p.id} type="button" onClick={() => setFocusPlayer(p.id)} data-testid={`admin-player-row-${p.id}`}
                className={`block w-full border-b border-[#243144] px-1 py-2 text-left text-xs hover:bg-[#1a2332] ${focusPlayer === p.id ? "bg-[#1a2332]" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="mono text-slate-200">{p.serial || "[deleted]"}</span>
                  {p.is_test && <span className="rounded bg-amber-500/20 px-1 text-[10px] text-amber-300">TEST</span>}
                  {(p.flags || []).map((f) => <span key={f} className="rounded bg-red-500/20 px-1 text-[10px] text-red-300">{f}</span>)}
                  <span className="ml-auto"><StatusPill s={p.stage} /></span>
                </div>
                <div className="mt-0.5 text-slate-500">{p.email || "—"} · {fmt(p.created_at)}</div>
              </button>
            ))}
            {!rows.length && <div className="py-4 text-xs text-slate-500">No players.</div>}
          </div>
        </Panel>
      </div>
      {focusPlayer ? <PlayerDetail id={focusPlayer} onChanged={search} /> : <div className="text-xs text-slate-500 p-4">Select a player.</div>}
    </div>
  );
}
